import { ArrowOutwardIcon, Button, CanvaLogo, Link } from '@sparemin/blockhead';
import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { H1, H2 } from 'components/Heading';

import LoadingOverlay from 'components/LoadingOverlay';
import useCanvaAuthenticator from 'hooks/useCanvaAuthenticator';
import useCanvaDesign, { UseCanvaDesignConfig } from 'hooks/useCanvaDesign';
import {
  clearCanvaDesignInProgressAction,
  isCanvaDesignInProgressSelector,
  isExportingCanvaDesignSelector,
} from 'redux/modules/canva';
import { getAspectRatioNameFromRatio } from 'utils/aspect-ratio';
import { block } from './utils';

export interface CreateTabContentsProps {
  aspectRatio: number;
  onImageCreated?: UseCanvaDesignConfig['onImageCreated'];
}

const CreateTabContents: React.FC<CreateTabContentsProps> = ({
  aspectRatio,
  onImageCreated,
}) => {
  const isDesignInProgress = useSelector(isCanvaDesignInProgressSelector);
  const isExportingCanvaDesign = useSelector(isExportingCanvaDesignSelector);

  const dispatch = useDispatch();

  const { authenticating, withAuthentication } = useCanvaAuthenticator({
    force: false,
  });

  const { reopenPopup, closePopup, createDesign } = useCanvaDesign({
    source: 'Editor',
    aspectRatioName: getAspectRatioNameFromRatio(aspectRatio),
    onImageCreated,
    onClose: () => dispatch(clearCanvaDesignInProgressAction),
  });

  const handleCancelCanvaDesign = useCallback(() => {
    dispatch(clearCanvaDesignInProgressAction);
    closePopup();
  }, [closePopup, dispatch]);

  const handleCanva = useCallback(() => {
    if (isDesignInProgress) {
      reopenPopup();
      return;
    }

    createDesign();
  }, [createDesign, isDesignInProgress, reopenPopup]);

  return (
    <div className={block('create-tab-contents')}>
      {isExportingCanvaDesign ? (
        <LoadingOverlay
          title="Importing from Canva"
          className={block('design-loading-overlay')}
        />
      ) : (
        <div className={block('create-tab-body')}>
          <CanvaLogo className={block('create-tab-icon')} />

          <H1 className={block('create-tab-heading')}>
            {isDesignInProgress
              ? 'Finish editing in Canva. When you close the tab, your design will be added here.'
              : 'Create an image'}
          </H1>

          {!isDesignInProgress && <H2>Easily create an image with Canva</H2>}

          <Button
            variant="solid"
            color={isDesignInProgress ? 'tertiary' : 'primary'}
            isLoading={authenticating}
            isDisabled={authenticating}
            startIcon={<ArrowOutwardIcon width={16} height={16} />}
            className={block('create-tab-cta')}
            onPress={withAuthentication(handleCanva)}
          >
            {isDesignInProgress
              ? 'Take me back to Canva'
              : 'Create an image in Canva'}
          </Button>

          {isDesignInProgress && (
            <Link
              as="button"
              elementType="button"
              typography="heading"
              level={2}
              size={12}
              letterSpacing="2px"
              weight="bold"
              textTransform="uppercase"
              className={block('create-tab-cancel-button')}
              onPress={handleCancelCanvaDesign}
            >
              Cancel
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default CreateTabContents;
